import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { AlertTriangle, Banknote, CheckCircle2, Upload } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import {
  useConfirmBankLetter,
  useGetDriverBankDetails,
  useUploadBankLetter,
} from '../../hooks/backend/drivers'
import { useGetRefundBankDetails, useUploadRefundBankLetter } from '../../hooks/backend/refunds'
import { writeErrorMessage } from '../../utils/writeError'

// The bank details block on an active driver's record.
//
// WHY A LETTER AND NOT A FORM. There is no way to type an account number into
// this panel. You upload the bank's own confirmation letter, the details are
// read off it, and you confirm what you can see. The constraint that makes that
// true lives in the database, not here: driver_bank_accounts.source_letter_id is
// NOT NULL. A driver substituting somebody else's account is a live fraud route
// in a fleet business, and the letter is what closes it.
//
// WHY THE FIELDS ARE STILL EDITABLE. Extraction proposes, a person disposes. If
// OCR misreads a digit the operator fixes it here, with the letter in front of
// them, and the corrected value is what gets saved.
//
// TWO DOORS, ONE PANEL. It opens from a driver record (applicationId) or from a
// refund (refundId). The second exists because the person a refund is owed to may
// never have been a driver, and the driver record's read starts from drivers
// (migration 1900). Only the read and the upload differ; confirming is the same
// call, bank_letter_decide_v1, so the control is the same control from both doors.

const MAX_BYTES = 10 * 1024 * 1024

const BANKS = [
  { code: 'capitec', label: 'Capitec Bank' },
  { code: 'fnb', label: 'First National Bank' },
  { code: 'absa', label: 'Absa Bank' },
  { code: 'standard_bank', label: 'Standard Bank' },
  { code: 'nedbank', label: 'Nedbank' },
  { code: 'tymebank', label: 'TymeBank' },
]

type Extracted = {
  bank_key: string | null
  account_holder: string | null
  account_number: string | null
  branch_code: string | null
  account_type: number | null
  confidence?: string
  method?: string
  warnings?: string[]
}

type LetterRow = {
  id: string
  original_filename: string | null
  status: string
  uploaded_at: string | null
  extracted: Extracted | null
  rejected_reason: string | null
}

type PanelRow = {
  person_id: string
  driver_name: string
  bank_name: string | null
  account_holder: string | null
  account_number: string | null
  branch_code: string | null
  account_type_label: string | null
  holder_matches_driver: boolean | null
  simplepay_synced_at: string | null
  panel_state: string
  // Only on the refund door. A refund is paid by EFT, never through SimplePay.
  is_simplepay_employee?: boolean
}

// canUpload only comes from the refund door (migration 1901). The driver record's
// door leaves it out, and there filing still needs canManage.
type PanelData = { panel: PanelRow | null; letters: LetterRow[]; canManage: boolean; canUpload?: boolean }

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      resolve(result.split(',')[1] ?? '')
    }
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file.'))
    reader.readAsDataURL(file)
  })
}

type Target = { applicationId: string; refundId?: never } | { refundId: string; applicationId?: never }

export function BankDetailsPanel(props: Target & { onChanged?: () => void }) {
  const forRefund = props.refundId != null
  // Both pairs of hooks are always called, because hooks may not be conditional;
  // only the pair for this door is ever triggered.
  const driverDetails = useGetDriverBankDetails<PanelData>()
  const refundDetails = useGetRefundBankDetails<PanelData>()
  const driverUpload = useUploadBankLetter()
  const refundUpload = useUploadRefundBankLetter()
  const details = forRefund ? refundDetails : driverDetails
  const upload = forRefund ? refundUpload : driverUpload
  const confirm = useConfirmBankLetter()
  const inputRef = useRef<HTMLInputElement>(null)
  const uid = useId()
  const { onChanged } = props

  const key = forRefund ? { refund_id: props.refundId } : { application_id: props.applicationId }
  const keyId = forRefund ? props.refundId : props.applicationId
  const trigger = details.trigger
  const load = useCallback(() => {
    void trigger(key, { skipCache: true })
    // key is rebuilt every render; keyId is what it is made of.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, keyId, forRefund])

  useEffect(() => {
    load()
  }, [load])

  const [file, setFile] = useState<File | null>(null)
  const [tooLarge, setTooLarge] = useState(false)
  const [draft, setDraft] = useState<Extracted | null>(null)
  const [draftLetterId, setDraftLetterId] = useState<string | null>(null)

  const data = details.data
  const panel = data?.panel ?? null
  const canManage = data?.canManage === true
  const canUpload = canManage || data?.canUpload === true

  // The most recent letter that a person still has to look at.
  const pending = (data?.letters ?? []).find((l) => l.status === 'extracted') ?? null

  // Cleared when no letter is waiting, not only on a successful confirm. Confirming
  // clears the draft, but the read still on screen holds the letter as 'extracted'
  // until the refetch lands, so this effect put the form straight back and nothing
  // ever took it away. Seen on the refund screen on 2 October 2026.
  useEffect(() => {
    if (!pending) {
      if (draftLetterId !== null) {
        setDraft(null)
        setDraftLetterId(null)
      }
      return
    }
    if (draftLetterId !== pending.id) {
      setDraftLetterId(pending.id)
      setDraft(pending.extracted ?? null)
    }
  }, [pending, draftLetterId])

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.files?.[0] ?? null
    setFile(next)
    setTooLarge(next != null && next.size > MAX_BYTES)
  }

  async function handleUpload() {
    if (!file || tooLarge) return
    try {
      const contentBase64 = await readAsBase64(file)
      await upload.trigger({
        ...key,
        filename: file.name,
        contentType: file.type || 'application/pdf',
        contentBase64,
      }).result
      toast.success('Letter uploaded and read. Check the details below before confirming.')
      setFile(null)
      if (inputRef.current) inputRef.current.value = ''
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    }
  }

  async function handleConfirm() {
    if (!draft || !draftLetterId) return
    try {
      const res = await confirm.trigger({
        letter_id: draftLetterId,
        bank_key: draft.bank_key ?? '',
        account_holder: draft.account_holder ?? '',
        account_number: draft.account_number ?? '',
        branch_code: draft.branch_code ?? '',
        account_type: draft.account_type ?? 1,
      }).result as { detail?: string } | null
      toast.success(res?.detail ?? 'Bank account set from the letter.')
      setDraft(null)
      setDraftLetterId(null)
      load()
      onChanged?.()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    }
  }

  async function handleReject() {
    if (!draftLetterId) return
    try {
      await confirm.trigger({
        letter_id: draftLetterId,
        bank_key: '',
        account_holder: '',
        account_number: '',
        branch_code: '',
        account_type: 1,
        reject_reason: 'Rejected on review by the operator.',
      }).result
      toast.success('Letter rejected. Nothing was changed.')
      setDraft(null)
      setDraftLetterId(null)
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    }
  }

  if (details.loading && !data) {
    return (
      <section className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner className="h-4 w-4" /> Loading bank details…
        </div>
      </section>
    )
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <Banknote className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">Bank details</h3>
      </div>

      {/* Current account, or the absence of one stated plainly. */}
      {panel?.account_number ? (
        <div className="mb-4 rounded-md border border-border bg-background p-3 text-sm">
          <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
            <div><span className="text-muted-foreground">Bank </span>{panel.bank_name ?? '—'}</div>
            <div><span className="text-muted-foreground">Type </span>{panel.account_type_label ?? '—'}</div>
            <div><span className="text-muted-foreground">Account </span>
              <span className="tabular-nums">{panel.account_number}</span></div>
            <div><span className="text-muted-foreground">Branch </span>
              <span className="tabular-nums">{panel.branch_code}</span></div>
            <div className="sm:col-span-2">
              <span className="text-muted-foreground">Holder </span>{panel.account_holder}
            </div>
          </div>
          {panel.holder_matches_driver === false && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              The name on the letter is not {forRefund ? 'this person' : 'this driver'}&rsquo;s name. Confirm who owns this account before paying into it.
            </p>
          )}
          {forRefund && panel.is_simplepay_employee === false ? (
            <p className="mt-2 text-xs text-muted-foreground">Not a SimplePay employee. A refund is paid by EFT to this account.</p>
          ) : !panel.simplepay_synced_at && (
            <p className="mt-2 text-xs text-muted-foreground">Not yet sent to SimplePay.</p>
          )}
        </div>
      ) : (
        <p className="mb-4 text-sm text-muted-foreground">
          {forRefund
            ? `No bank account on file for ${panel?.driver_name ?? 'this person'}, so the refund cannot be paid. Upload their bank confirmation letter, or a bank statement in their name.`
            : 'No bank account on file, so this driver cannot be paid by transfer. Upload their bank confirmation letter.'}
        </p>
      )}

      {/* A letter that has been read and is waiting for a person. */}
      {draft && canManage && (
        <div className="mb-4 rounded-md border border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand))]/5 p-3">
          <p className="mb-2 text-sm font-medium text-foreground">
            Read from the document. Check every figure against it before confirming.
          </p>
          {(pending?.extracted?.warnings ?? []).map((w, i) => (
            <p key={i} className="mb-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{w}
            </p>
          ))}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor={`${uid}-bank`}>Bank</Label>
              <NativeSelect
                id={`${uid}-bank`}
                value={draft.bank_key ?? ''}
                onChange={(e) => setDraft({ ...draft, bank_key: e.target.value })}
              >
                <option value="">Choose a bank</option>
                {BANKS.map((b) => <option key={b.code} value={b.code}>{b.label}</option>)}
              </NativeSelect>
            </div>
            <div>
              <Label htmlFor={`${uid}-type`}>Account type</Label>
              <NativeSelect
                id={`${uid}-type`}
                value={String(draft.account_type ?? 1)}
                onChange={(e) => setDraft({ ...draft, account_type: Number(e.target.value) })}
              >
                <option value="1">Cheque or current</option>
                <option value="2">Savings</option>
              </NativeSelect>
            </div>
            <div>
              <Label htmlFor={`${uid}-acct`}>Account number</Label>
              <Input
                id={`${uid}-acct`}
                inputMode="numeric"
                value={draft.account_number ?? ''}
                onChange={(e) => setDraft({ ...draft, account_number: e.target.value.replace(/\D/g, '') })}
              />
            </div>
            <div>
              <Label htmlFor={`${uid}-branch`}>Branch code</Label>
              <Input
                id={`${uid}-branch`}
                inputMode="numeric"
                value={draft.branch_code ?? ''}
                onChange={(e) => setDraft({ ...draft, branch_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor={`${uid}-holder`}>Account holder</Label>
              <Input
                id={`${uid}-holder`}
                value={draft.account_holder ?? ''}
                onChange={(e) => setDraft({ ...draft, account_holder: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={handleConfirm} disabled={confirm.loading}>
              {confirm.loading ? <Spinner className="mr-2 h-4 w-4" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              Confirm and set account
            </Button>
            <Button size="sm" variant="outline" onClick={handleReject} disabled={confirm.loading}>
              Reject this letter
            </Button>
          </div>
        </div>
      )}

      {/* Read, but this person may not confirm it. Said plainly so they know it is
          not stuck and who it is waiting for. */}
      {pending && !canManage && (
        <p className="mb-4 rounded-md border border-border bg-background p-3 text-sm text-muted-foreground">
          {pending.original_filename ?? 'A document'} has been read and is waiting for someone with bank details permission to check it and confirm the account. Until then nothing can be paid.
        </p>
      )}

      {/* Upload. */}
      {canUpload ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0 flex-1">
            <Label htmlFor={`${uid}-file`}>Bank confirmation letter or bank statement</Label>
            <input
              id={`${uid}-file`}
              ref={inputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              onChange={handleFile}
              className="mt-1 block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:border-border file:bg-background file:px-3 file:py-1.5 file:text-sm file:text-foreground"
            />
            {tooLarge && (
              <p className="mt-1 text-xs text-destructive">That file is larger than 10 MB.</p>
            )}
          </div>
          <Button size="sm" onClick={handleUpload} disabled={!file || tooLarge || upload.loading}>
            {upload.loading ? <Spinner className="mr-2 h-4 w-4" /> : <Upload className="mr-2 h-4 w-4" />}
            Upload and read
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {forRefund
            ? 'Only someone who holds bank details permission on their Cockpit access can file a bank letter here.'
            : <>Only Luyanda, Yolisa or this driver&rsquo;s own manager can change bank details.</>}
        </p>
      )}

      {/* History, short and factual. */}
      {(data?.letters ?? []).length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
          {(data?.letters ?? []).slice(0, 4).map((l) => (
            <li key={l.id}>
              {l.original_filename ?? 'letter'} · {l.status}
              {l.rejected_reason ? ` · ${l.rejected_reason}` : ''}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
